import React from 'react';
import {
    Card, CardContent, CardActions, Typography, Button, Box, useTheme
} from '@mui/material';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import { ArrowForwardRounded, CloseRounded } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BrandContext } from '../../App';

const ProgressModal = ({ trainingProgram, sideMenuOpen }) => {
    
    const theme = useTheme();
    const { user } = React.useContext(BrandContext);
    const navigate = useNavigate();

    const [programCompleted, setProgramCompleted] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const [open, setOpen] = React.useState(false);

    // 1. Progress bucket (e.g., 0–9%, 10–19%, etc.)
    const progressBucket = Math.floor(progress / 10);

    React.useEffect(() => {
        if (!trainingProgram?.chapters) return;

        let totalScenarios = 0;
        let completedScenarios = 0;

        trainingProgram.chapters.forEach(chapter => {
            if (chapter?.scenarios) {
                chapter.scenarios.forEach(scenario => {
                    if (scenario) {
                        totalScenarios += 1;
                        if (scenario.completed) {
                            completedScenarios += 1;
                        }
                    }
                });
            }
        });

        const percentage = totalScenarios > 0 ? (completedScenarios / totalScenarios) * 100 : 0;
        setProgress(percentage);
        if (percentage >= 100) {
            setProgramCompleted(true);
        }
    }, [trainingProgram]);

    React.useEffect(() => {
        const dismissedBucket = localStorage.getItem('progressModalDismissedAt');
        setOpen(!programCompleted && (dismissedBucket === null || Number(dismissedBucket) < progressBucket))
    }, [progressBucket, programCompleted]);

    const handleClose = () => {
        localStorage.setItem('progressModalDismissedAt', progressBucket.toString());
        setOpen(false);
    };

    const getNextScenario = () => {
        if (!trainingProgram?.chapters) return null;
        for (const chapter of trainingProgram.chapters) {
            if (!chapter?.scenarios) continue;
            for (const scenario of chapter.scenarios) {
                if (scenario && scenario.completed !== true) {
                    return scenario;
                }
            }
        }
        return null;
    };

    const nextScenario = getNextScenario();

    return open && sideMenuOpen ? (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                backgroundColor: theme.palette.background.primary,
                border: `1px solid ${theme.palette.border.tertiary}`,
                padding: theme.customSpacing.xl,
                margin: `0 ${theme.customSpacing.lg}`,
            }}
        >
            <CardContent sx={{ padding: 0 }}>
                <Box sx={{ height: '64px', width: '64px', mb: theme.customSpacing.lg }}>
                    <CircularProgressbar
                        value={progress}
                        text={`${Math.round(progress)}%`}
                        strokeWidth={12.5}
                        styles={buildStyles({
                            strokeLinecap: 'round',
                            pathColor: theme.palette.border.brand,
                            trailColor: theme.palette.border.secondary,
                            textColor: theme.palette.text.primary,
                            pathTransition: 'stroke-dashoffset 0.5s ease 0s',
                        })}
                    />
                </Box>
                <CloseRounded
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: theme.customSpacing.xl,
                        top: theme.customSpacing.xl,
                        color: '#BFBFCC',
                        fontSize: 28,
                        cursor: 'pointer',
                    }}
                />
                <Typography
                    sx={{
                        color: theme.palette.text.primary,
                        fontSize: theme.typography.textSM.fontSize,
                        fontWeight: theme.typography.fontWeightMedium,
                        whiteSpace: 'normal',
                    }}
                >
                    {trainingProgram?.name}
                </Typography>
                <Typography
                    sx={{
                        color: theme.palette.text.tertiary,
                        fontSize: theme.typography.textSM.fontSize,
                        fontWeight: theme.typography.fontWeightLight,
                        whiteSpace: 'normal',
                        mb: theme.customSpacing.xl,
                    }}
                >
                    You're making great progress!
                    Keep going{user?.first_name ? ` ${user.first_name}` : ''},
                    you're one step closer to mastering this. 🚀
                </Typography>
            </CardContent>
            <CardActions sx={{ padding: 0 }}>
                {nextScenario && (
                    <Button
                        onClick={() => navigate(`/dashboard/simulate/${nextScenario.id}`)}
                        endIcon={<ArrowForwardRounded />}
                        sx={{
                            textTransform: 'none',
                            color: theme.palette.border.brand,
                            padding: 0,
                        }}
                    >
                        Next {nextScenario.resources === null ? 'Simulation' : 'Skills Development Session'}
                    </Button>
                )}
            </CardActions>
        </Card>
    ) : null;
};

export default ProgressModal;
